"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { trackButtonClick } from "@/lib/gtag";
import { cn } from "@/lib/utils";

interface NavbarProps {
  isLoggedIn?: boolean;
  userEmail?: string;
  userRole?: "ADMIN" | "STUDENT";
  onLogout?: () => void;
  theme?: "dark" | "light";
}

export default function Navbar({
  isLoggedIn = false,
  userEmail,
  userRole,
  onLogout,
  theme = "light",
}: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [storedIsLoggedIn, setStoredIsLoggedIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;
    setStoredIsLoggedIn(window.localStorage.getItem("isLoggedIn") === "true");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const effectiveIsLoggedIn = isLoggedIn || storedIsLoggedIn;

  const handleLogout = () => {
    if (onLogout) onLogout();
    if (typeof document !== "undefined") {
      document.cookie = "auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    }
    router.push("/login");
  };

  const navLinkClass = (path: string) => cn(
    "cursor-pointer text-sm font-medium transition-colors hover:text-indigo-600",
    pathname === path ? "text-black" : "text-zinc-700"
  );

  return (
    <>
      <nav className={cn(
        "fixed w-full z-50 transition-all duration-300",
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-zinc-200/50 py-4 shadow-sm"
          : "bg-transparent border-transparent py-6"
      )}>
        <div className="max-w-5xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => router.push(effectiveIsLoggedIn ? "/profile" : "/")}
          >
            <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-zinc-100">
              <Image src="/new-logo.png" alt="Senera" fill className="object-cover" unoptimized />
            </div>
          </div>

          {/* Desktop Menu & Right Side / CTA */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {effectiveIsLoggedIn ? (
                <>
                  <button onClick={() => router.push("/profile")} className={navLinkClass("/profile")}>Home</button>
                  <button onClick={() => router.push(userRole === "ADMIN" ? "/admin" : "/profile")} className={navLinkClass("/profile")}>Profile</button>
                  <button onClick={() => router.push("/compete")} className={navLinkClass("/compete")}>Compete</button>
                </>
              ) : (
                <>
                  <Link href="/#services" className={navLinkClass("/#services")}>Services</Link>
                  <Link href="/case-studies" className={navLinkClass("/case-studies")}>Case Studies</Link>
                  <Link href="/blog" className={navLinkClass("/blog")}>Blog</Link>
                  <Link href="/about" className={navLinkClass("/about")}>About</Link>
                  <a href="https://cut-eris-55d.notion.site/32191048854080808b5ecef98c7eca1b?pvs=105" className={navLinkClass("")}>Careers</a>
                </>
              )}
            </div>

            <div className="flex items-center gap-4">
              {effectiveIsLoggedIn ? (
                <>
                  <span className="text-zinc-600 text-sm hidden lg:block">{userEmail}</span>
                  <button
                    onClick={() => router.push("/profile")}
                    className="cursor-pointer px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                  >
                    Dashboard
                  </button>
                </>
              ) : (
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm shadow-indigo-100"
                >
                  Book Your Free Strategy Call
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Toggle */}
          <button
            className={cn("cursor-pointer md:hidden p-2", theme === 'light' ? "text-black" : "text-white")}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Content */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-zinc-200 p-4 flex flex-col gap-4 shadow-2xl">
            {/* Simplified logic for mobile for brevity - kept consistent */}
            {effectiveIsLoggedIn ? (
              <>
                <button onClick={() => router.push("/profile")} className="cursor-pointer text-left text-zinc-700 hover:text-indigo-600 py-2">Home</button>
                <button onClick={handleLogout} className="cursor-pointer text-left text-red-500 hover:text-red-600 py-2">Logout</button>
              </>
            ) : (
              <>
                <Link href="/#services" className="cursor-pointer py-2 text-zinc-700 hover:text-indigo-600">Services</Link>
                <Link href="/case-studies" className="cursor-pointer py-2 text-zinc-700 hover:text-indigo-600">Case Studies</Link>
                <Link href="/blog" className="cursor-pointer py-2 text-zinc-700 hover:text-indigo-600">Blog</Link>
                <Link href="/about" className="cursor-pointer py-2 text-zinc-700 hover:text-indigo-600">About</Link>
                <a href="https://cut-eris-55d.notion.site/32191048854080808b5ecef98c7eca1b?pvs=105" className="cursor-pointer py-2 text-zinc-700 hover:text-indigo-600">Careers</a>
                <Link href="/contact" className="inline-flex items-center justify-center w-full py-3 rounded-lg font-medium mt-2 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm">
                  Book Your Free Strategy Call
                </Link>
              </>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
